import type {
  Completion,
  CompletionContext,
  CompletionResult,
} from "@codemirror/autocomplete";
import { syntaxTree } from "@codemirror/language";
import type { EditorState } from "@codemirror/state";
import type { SyntaxNode } from "@lezer/common";
import {
  lookupProviders,
  lookupResources,
  lookupSchemas,
  providerFitsRole,
} from "./pipelineLint";

// Autosuggestions for the Full pipeline code editor. /studio's Monaco only
// had incidental word-based suggestions for JSON; here the completions are
// semantic: pipeline keys per object, Encoding values, and live service /
// resource / schema names from the engine (same TTL-cached lookups as the
// linter).

const TOP_KEYS = ["name", "source", "middlewares", "sinks", "schemas"];
const STAGE_KEYS = [
  "service_name",
  "resource",
  "input_encoding",
  "output_encoding",
];
const SCHEMA_KEYS = ["id", "service_name", "resource"];
const ENCODINGS = ["bson", "json", "avro"];
const STAGE_SECTIONS = new Set(["source", "middlewares", "sinks"]);

// Domain aliases naming what the primitive arguments mean.
type JsonKey = string;
/** A top-level pipeline section name, or null for the document root. */
type SectionName = string | null;

export interface CompletionLookups {
  providers: () => Promise<Map<string, string> | null>;
  resources: (serviceName: string) => Promise<Set<string> | null>;
  schemas: (serviceName: string) => Promise<Set<string> | null>;
}

export function propertyKey(
  state: EditorState,
  property: SyntaxNode,
): JsonKey | null {
  const keyNode = property.firstChild;
  if (!keyNode) return null;
  const text = state.doc.sliceString(keyNode.from, keyNode.to);
  return text.startsWith('"') ? text.replace(/^"|"$/g, "") : null;
}

// The Property node a stage/schema object hangs off: either directly
// (source) or through the section array (middlewares/sinks/schemas).
function enclosingProperty(parent: SyntaxNode): SyntaxNode | null {
  const holder = parent.name === "Array" ? parent.parent : parent;
  if (!holder) return null;
  return holder.name === "Property" ? holder : null;
}

function isTopLevelProperty(holder: SyntaxNode): boolean {
  const top = holder.parent;
  if (top?.name !== "Object") return false;
  return top.parent?.name === "JsonText";
}

// Which top-level section an object belongs to: the stage/schema objects sit
// either directly under a top-level property (source) or as elements of a
// top-level array (middlewares/sinks/schemas). Deeper nesting has no
// pipeline meaning — no suggestions there.
// Shared with emptySlotHints.ts (the ghost hints need the same section rule).
export function sectionOf(
  state: EditorState,
  object: SyntaxNode,
): { section: SectionName } | null {
  const parent = object.parent;
  if (!parent) return { section: null };
  if (parent.name === "JsonText") return { section: null };
  const holder = enclosingProperty(parent);
  if (!holder) return null;
  if (!isTopLevelProperty(holder)) return null;
  return { section: propertyKey(state, holder) };
}

function keysForSection(section: SectionName): string[] | null {
  if (section === null) return TOP_KEYS;
  if (STAGE_SECTIONS.has(section)) return STAGE_KEYS;
  if (section === "schemas") return SCHEMA_KEYS;
  return null;
}

function existingKeys(state: EditorState, object: SyntaxNode): Set<string> {
  const keys = new Set<string>();
  for (
    let property = object.firstChild;
    property;
    property = property.nextSibling
  ) {
    if (property.name !== "Property") continue;
    const key = propertyKey(state, property);
    if (key) keys.add(key);
  }
  return keys;
}

/** Value of a sibling string property (e.g. the stage's service_name). */
function siblingString(
  state: EditorState,
  object: SyntaxNode,
  key: JsonKey,
): string | null {
  for (
    let property = object.firstChild;
    property;
    property = property.nextSibling
  ) {
    if (property.name !== "Property") continue;
    if (propertyKey(state, property) !== key) continue;
    const value = property.lastChild;
    if (!value || value.name !== "String") return null;
    return state.doc.sliceString(value.from, value.to).replace(/^"|"$/g, "");
  }
  return null;
}

function stringInterior(
  state: EditorState,
  node: SyntaxNode,
): { from: number; to: number } {
  const text = state.doc.sliceString(node.from, node.to);
  return {
    from: node.from + (text.startsWith('"') ? 1 : 0),
    to: node.to - (text.length > 1 && text.endsWith('"') ? 1 : 0),
  };
}

// A service only makes sense where its provider can be staged — same rules
// as the linter's "did you mean" fixes (providerFitsRole).
const SECTION_ROLES = new Map([
  ["source", "source"],
  ["middlewares", "middleware"],
  ["sinks", "sink"],
  ["schemas", "schema"],
] as const);

function providerFitsSection(section: SectionName, provider: string): boolean {
  const role =
    section === null ? undefined : SECTION_ROLES.get(section as never);
  return role !== undefined && providerFitsRole(role, provider);
}

// Everything a value completion needs to know about where it's asked.
interface ValueSite {
  state: EditorState;
  object: SyntaxNode;
  section: SectionName;
  lookups: CompletionLookups;
}

function asOptions(
  labels: Iterable<string>,
  type: Completion["type"],
): Completion[] {
  return [...labels].map((label) => ({ label, type }));
}

async function serviceNameOptions(
  site: ValueSite,
): Promise<Completion[] | null> {
  const providers = await site.lookups.providers();
  if (!providers) return null;
  const fitting = [...providers.entries()]
    .filter(([, provider]) => providerFitsSection(site.section, provider))
    .map(([name]) => name);
  return asOptions(fitting, "variable");
}

async function resourceOptions(site: ValueSite): Promise<Completion[] | null> {
  const serviceName = siblingString(site.state, site.object, "service_name");
  if (!serviceName) return null;
  const known =
    site.section === "schemas"
      ? await site.lookups.schemas(serviceName)
      : await site.lookups.resources(serviceName);
  if (!known) return null;
  return asOptions(known, "variable");
}

async function valueOptions(
  site: ValueSite,
  key: JsonKey,
): Promise<Completion[] | null> {
  if (key === "output_encoding") return asOptions(ENCODINGS, "constant");
  if (key === "input_encoding") return asOptions(ENCODINGS, "constant");
  if (key === "service_name") return serviceNameOptions(site);
  if (key === "resource") return resourceOptions(site);
  return null;
}

// Key suggestions for an object: unused section keys (plus the key being
// retyped, so it doesn't vanish from under the cursor).
function keyOptions(
  state: EditorState,
  object: SyntaxNode,
  section: SectionName,
  current: JsonKey | null,
): Completion[] | null {
  const keys = keysForSection(section);
  if (!keys) return null;
  const present = existingKeys(state, object);
  const usable = keys.filter((key) => key === current || !present.has(key));
  return asOptions(usable, "property");
}

function resultOf(
  from: number,
  to: number | undefined,
  options: Completion[] | null,
  validFor: RegExp,
): CompletionResult | null {
  if (!options) return null;
  if (options.length === 0) return null;
  return { from, to, options, validFor };
}

// Where a string-node completion is asked: its property, object, section
// and the replaceable range inside the quotes.
function stringSite(
  state: EditorState,
  node: SyntaxNode,
): {
  property: SyntaxNode;
  object: SyntaxNode;
  section: SectionName;
  from: number;
  to: number;
} | null {
  const property = node.parent;
  if (property?.name !== "Property") return null;
  const object = property.parent;
  if (object?.name !== "Object") return null;
  const info = sectionOf(state, object);
  if (!info) return null;
  return {
    property,
    object,
    section: info.section,
    ...stringInterior(state, node),
  };
}

// Inside a string — either a key being typed or a value.
async function completeInString(
  state: EditorState,
  node: SyntaxNode,
  lookups: CompletionLookups,
): Promise<CompletionResult | null> {
  const at = stringSite(state, node);
  if (!at) return null;

  const isKey = node.name === "PropertyName" || at.property.firstChild === node;
  if (isKey) {
    const current = propertyKey(state, at.property);
    const options = keyOptions(state, at.object, at.section, current);
    return resultOf(at.from, at.to, options, /^[\w-]*$/);
  }

  const key = propertyKey(state, at.property);
  if (!key) return null;
  const site = { state, object: at.object, section: at.section, lookups };
  return resultOf(at.from, at.to, await valueOptions(site, key), /^[\w.-]*$/);
}

// Bare position inside an object — offer quoted keys.
function completeBareKey(
  state: EditorState,
  node: SyntaxNode,
  pos: number,
): CompletionResult | null {
  const object = node.name === "Object" ? node : node.parent;
  if (object?.name !== "Object") return null;
  const info = sectionOf(state, object);
  if (!info) return null;
  const keys = keysForSection(info.section);
  if (!keys) return null;
  const present = existingKeys(state, object);
  const options = keys
    .filter((key) => !present.has(key))
    .map(
      (key): Completion => ({
        label: key,
        type: "property",
        apply: `"${key}": `,
      }),
    );
  return resultOf(pos, undefined, options, /^[\w-]*$/);
}

const STRING_NODES = new Set(["String", "PropertyName"]);
const OBJECT_NODES = new Set(["Object", "{"]);

/** Builds the completion source; the default export wires the live lookups. */
export function makePipelineCompletion(lookups: CompletionLookups) {
  return async function pipelineCompletion(
    ctx: CompletionContext,
  ): Promise<CompletionResult | null> {
    const state = ctx.state;
    const node = syntaxTree(state).resolveInner(ctx.pos, -1);
    if (STRING_NODES.has(node.name)) {
      return completeInString(state, node, lookups);
    }
    if (OBJECT_NODES.has(node.name)) {
      return completeBareKey(state, node, ctx.pos);
    }
    return null;
  };
}

export const pipelineCompletion = makePipelineCompletion({
  providers: lookupProviders,
  resources: lookupResources,
  schemas: lookupSchemas,
});
